// Zoho OAuth types
interface ZohoTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

interface ZohoUserProfile {
  First_Name: string;
  Last_Name: string;
  Email: string;
  ZASID: string;
  Display_Name: string;
  Country: string;
  Language: string;
  Timezone: string;
}

// Zoho OAuth configuration
// ⚠️ SECURITY WARNING: clientSecret should NEVER be in frontend code
// This OAuth flow should be handled by your backend server
const ZOHO_CONFIG = {
  clientId: import.meta.env.VITE_ZOHO_CLIENT_ID || 'your-zoho-client-id',
  // ❌ REMOVE THIS - Client secrets must be kept on backend only
  // clientSecret should only exist on your backend server
  redirectUri: import.meta.env.VITE_ZOHO_REDIRECT_URI || 'http://localhost:3002/auth/zoho/callback',
  scope: 'ZohoMail.accounts.READ ZohoMail.messages.READ',
  responseType: 'code',
  accessType: 'offline',
  accountsServer: 'https://accounts.zoho.com'
};

export class ZohoOAuthService {
  /**
   * Generate the Zoho OAuth authorization URL
   */
  static getAuthUrl(state?: string): string {
    const params = new URLSearchParams({
      client_id: ZOHO_CONFIG.clientId,
      scope: ZOHO_CONFIG.scope,
      response_type: ZOHO_CONFIG.responseType,
      redirect_uri: ZOHO_CONFIG.redirectUri,
      access_type: ZOHO_CONFIG.accessType,
      ...(state && { state })
    });

    return `${ZOHO_CONFIG.accountsServer}/oauth/v2/auth?${params.toString()}`;
  }

  /**
   * Initiate Zoho OAuth login flow
   */
  static initiateLogin(): void {
    // Generate a random state for security
    const state = btoa(Math.random().toString()).substring(0, 12);
    
    // Store state in sessionStorage to verify later
    sessionStorage.setItem('zoho_oauth_state', state);
    
    // Redirect to Zoho OAuth
    const authUrl = this.getAuthUrl(state);
    window.location.href = authUrl;
  }

  /**
   * Handle OAuth callback (extract code and exchange for token)
   */
  static async handleCallback(code: string, state: string): Promise<ZohoTokenResponse> {
    // Verify state matches
    const storedState = sessionStorage.getItem('zoho_oauth_state');
    if (state !== storedState) {
      throw new Error('Invalid OAuth state parameter');
    }

    // Clear stored state
    sessionStorage.removeItem('zoho_oauth_state');

    // ⚠️ SECURITY ISSUE: Token exchange MUST happen on backend
    // Frontend should send the 'code' to backend, backend exchanges it with client_secret
    // For now, this will fail without client_secret (which is correct - don't expose secrets)
    
    // TODO: Send code to your backend endpoint like: POST /api/auth/zoho/callback
    // Backend should handle the token exchange and return the user data
    throw new Error('Zoho OAuth token exchange must be handled by backend server for security');

    /* OLD INSECURE CODE - DO NOT USE
    const tokenResponse = await fetch(`${ZOHO_CONFIG.accountsServer}/oauth/v2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        client_id: ZOHO_CONFIG.clientId,
        client_secret: ZOHO_CONFIG.clientSecret, // ❌ NEVER expose secrets in frontend
        redirect_uri: ZOHO_CONFIG.redirectUri,
        grant_type: 'authorization_code',
        code: code
      })
    });

    if (!tokenResponse.ok) {
      throw new Error('Failed to exchange code for token');
    }

    const tokenData: ZohoTokenResponse = await tokenResponse.json();
    
    // Store tokens securely
    localStorage.setItem('zoho_access_token', tokenData.access_token);
    if (tokenData.refresh_token) {
      localStorage.setItem('zoho_refresh_token', tokenData.refresh_token);
    }

    return tokenData;
    */
  }

  /**
   * Get user profile information from Zoho
   */
  static async getUserProfile(): Promise<ZohoUserProfile> {
    const accessToken = localStorage.getItem('zoho_access_token');
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const response = await fetch('https://accounts.zoho.com/oauth/user/info', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }

    return await response.json();
  }

  /**
   * Check if user is authenticated with Zoho
   */
  static isAuthenticated(): boolean {
    return !!localStorage.getItem('zoho_access_token');
  }

  /**
   * Logout from Zoho (clear tokens)
   */
  static logout(): void {
    localStorage.removeItem('zoho_access_token');
    localStorage.removeItem('zoho_refresh_token');
  }

  /**
   * Refresh access token using refresh token
   */
  static async refreshToken(): Promise<ZohoTokenResponse> {
    // ⚠️ SECURITY ISSUE: Token refresh MUST happen on backend
    // Frontend should send the refresh_token to backend, backend exchanges it with client_secret
    // For now, this will fail without client_secret (which is correct - don't expose secrets)
    
    // TODO: Send refresh token to your backend endpoint like: POST /api/auth/zoho/refresh
    // Backend should handle the token refresh and return the new access token
    throw new Error('Zoho OAuth token refresh must be handled by backend server for security');

    /* OLD INSECURE CODE - DO NOT USE
    const refreshToken = localStorage.getItem('zoho_refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${ZOHO_CONFIG.accountsServer}/oauth/v2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        client_id: ZOHO_CONFIG.clientId,
        client_secret: ZOHO_CONFIG.clientSecret, // ❌ NEVER expose secrets in frontend
        refresh_token: refreshToken,
        grant_type: 'refresh_token'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const tokenData: ZohoTokenResponse = await response.json();
    localStorage.setItem('zoho_access_token', tokenData.access_token);
    
    return tokenData;
    */
  }
}

// Environment variables template for .env file:
/*
VITE_ZOHO_CLIENT_ID=your_zoho_client_id_here
VITE_ZOHO_CLIENT_SECRET=your_zoho_client_secret_here
VITE_ZOHO_REDIRECT_URI=http://localhost:3001/auth/zoho/callback
*/
